import { useCallback, useEffect } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';

import { useChatAnalytics } from 'app/analytics';
import { ANALYTICS_EVENTS_SOURCES } from 'app/analytics/config';
import useToggle from 'shared_DEPRECATED/hooks/useToggle';

export const useChatToggle = (
  externalOpen?: boolean,
  externalToggle?: () => void
): [boolean, () => void] => {
  const [searchParams] = useSearchParams();
  const isChatOpenOnPageLoad = Boolean(searchParams.get('openChat'));
  const [internalOpen, toggleInternalOpen] = useToggle();
  const { state, pathname } = useLocation();
  const navigate = useNavigate();
  const isChatOpenFromParticipantsList = state?.openChat;

  const { sendChatOverlayOpenAnalyticsEvent } = useChatAnalytics();

  // Use external state if provided (controlled mode), otherwise use internal state
  const isControlled = externalOpen !== undefined && externalToggle !== undefined;
  const open = isControlled ? externalOpen : internalOpen;
  const toggleOpen = isControlled ? externalToggle : toggleInternalOpen;

  const toggleChatOpen = useCallback(() => {
    toggleOpen();

    if (!open) {
      sendChatOverlayOpenAnalyticsEvent({
        source: isChatOpenFromParticipantsList
          ? ANALYTICS_EVENTS_SOURCES.PARTICIPANTS_LIST
          : ANALYTICS_EVENTS_SOURCES.PARTICIPANT_PAGE,
      });
    }
  }, [
    isChatOpenFromParticipantsList,
    open,
    toggleOpen,
    sendChatOverlayOpenAnalyticsEvent,
  ]);

  // Only auto-open from URL/navigation state in uncontrolled mode
  useEffect(() => {
    if (
      !isControlled &&
      (isChatOpenFromParticipantsList || isChatOpenOnPageLoad) &&
      !open
    ) {
      toggleChatOpen();
      navigate(pathname, {
        replace: true,
        state: { ...state, openChat: false },
      });
    }
  }, [
    isControlled,
    navigate,
    open,
    pathname,
    state,
    isChatOpenFromParticipantsList,
    isChatOpenOnPageLoad,
    toggleChatOpen,
  ]);

  return [open, toggleChatOpen];
};
